// @ts-nocheck
/* Starter Custom File */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { resolveColor } from "@insite/mobius/utilities";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import getSourceTypeField, {
    SourceType,
    sourceTypeOptions as videoSourceTypeOptions,
} from "@store/Framework/Common/Fields/Toolkit/sourceType";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import getVerticalAlignmentField, {
    verticalAlignmentOptions,
} from "@store/Framework/Common/Fields/Toolkit/verticalAlignment";
import Video, { VideoPresentationProps } from "@store/Library/Components/Toolkit/Video/Video";
import {
    videoPosterField,
    videoSourceMediaLibraryField,
    videoSourceUrlField,
} from "@store/Library/Components/Toolkit/Video/videoFields";
import { typographyInvertColorStyles } from "@store/Themes/common/mixins/typography";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@toolkit/Framework/Common/Utilities/dataOptions";
import getImageSize from "@toolkit/Framework/Common/Utilities/getImageSize";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import overrideProps from "@toolkit/Framework/Common/Utilities/overrideProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { LiteralUnion } from "@toolkit/Framework/Types/Common";
import getFocalPointStyles from "@toolkit/Modules/mobius/utilities/getFocalPointStyles";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import Color from "color";
import * as cssLinter from "css";
import React, { FC, ReactNode, useMemo } from "react";
import { css } from "styled-components";

const defaultBackgroundColor = "common.accent";
const defaultOverlayColor = "rgba(0, 0, 0, 0.9)";
const mobileBreakpoint: BreakpointKey = "xs";

const enum fields {
    backgroundColor = "backgroundColor",
    customCSS = "customCSS",
    customHeight = "customHeight",
    focalPoint = "focalPoint",
    fullWidth = "fullWidth",
    gridWidth = "gridWidth",
    hasMobileSettings = "hasMobileSettings",
    hasMobileVideo = "hasMobileVideo",
    hasVideo = "hasVideo",
    height = "height",
    horizontalAlignment = "horizontalAlignment",
    image = "image",
    invertColor = "invertColor",
    mobileFocalPoint = "mobileFocalPoint",
    mobileImage = "mobileImage",
    mobileSpacingPercent = "mobileSpacingPercent",
    mobileVideoPoster = "mobileVideoPoster",
    mobileVideoSourceMediaLibrary = "mobileVideoSourceMediaLibrary",
    mobileVideoSourceUrl = "mobileVideoSourceUrl",
    mobileVideoSourceType = "mobileVideoSourceType",
    overlayAlignment = "overlayAlignment",
    overlayColor = "overlayColor",
    overlayLength = "overlayLength",
    overlayPadding = "overlayPadding",
    overlayRadius = "overlayRadius",
    padding = "padding",
    scalingHeight = "scalingHeight",
    scalingWidth = "scalingWidth",
    showOverlay = "showOverlay",
    variant = "variant",
    verticalAlignment = "verticalAlignment",
    videoPoster = "videoPoster",
    videoSourceMediaLibrary = "videoSourceMediaLibrary",
    videoSourceUrl = "videoSourceUrl",
    videoSourceType = "videoSourceType",
}

type FocalPoint = Parameters<typeof getFocalPointStyles>[0];
const videoSourceTypeField = getSourceTypeField(fields.videoSourceType, { hideEmbedOption: true });

export interface BannerFields {
    [fields.backgroundColor]: string;
    [fields.customCSS]: string;
    [fields.customHeight]: string;
    [fields.focalPoint]: FocalPoint;
    [fields.fullWidth]: boolean;
    [fields.gridWidth]: string;
    [fields.hasMobileSettings]: boolean;
    [fields.hasMobileVideo]: boolean;
    [fields.hasVideo]: boolean;
    [fields.height]: string;
    [fields.horizontalAlignment]: string;
    [fields.image]: string;
    [fields.invertColor]: boolean;
    [fields.mobileFocalPoint]: FocalPoint;
    [fields.mobileImage]: string;
    [fields.mobileSpacingPercent]: number;
    [fields.mobileVideoPoster]: string;
    [fields.mobileVideoSourceMediaLibrary]: string;
    [fields.mobileVideoSourceUrl]: string;
    [fields.mobileVideoSourceType]: SourceType;
    [fields.overlayAlignment]: string;
    [fields.overlayColor]: string;
    [fields.overlayLength]: string;
    [fields.overlayPadding]: string;
    [fields.overlayRadius]: string;
    [fields.padding]: string;
    [fields.scalingHeight]: string;
    [fields.scalingWidth]: string;
    [fields.showOverlay]: boolean;
    [fields.variant]: string;
    [fields.verticalAlignment]: string;
    [fields.videoPoster]: string;
    [fields.videoSourceMediaLibrary]: string;
    [fields.videoSourceUrl]: string;
    [fields.videoSourceType]: SourceType;
}

const customHeightField = getCssUnitField(fields.customHeight);
const gridWidthField = getNumberTextField(fields.gridWidth, { integer: true, min: 0, max: 12 });
const horizontalAlignmentField = getTextAlignmentField(fields.horizontalAlignment);
const overlayLengthField = getNumberTextField(fields.overlayLength, { min: -1 });
const overlayPaddingField = getCssUnitField(fields.overlayPadding, { shorthands: ["top", "right", "bottom", "left"] });
const overlayRadiusField = getCssUnitField(fields.overlayRadius, {
    shorthands: ["top-left", "top-right", "bottom-right", "bottom-left"],
});
const paddingField = getCssUnitField(fields.padding, { shorthands: ["top", "right", "bottom", "left"] });
const scalingHeightField = getNumberTextField(fields.scalingHeight, { integer: true, min: 0 });
const scalingWidthField = getNumberTextField(fields.scalingWidth, { integer: true, min: 0 });
const verticalAlignmentField = getVerticalAlignmentField(fields.verticalAlignment);

export type BannerHeightOption = DataOption;
export interface BannerHeightOptionMapper {
    auto: BannerHeightOption;
    full: BannerHeightOption;
    half: BannerHeightOption;
    oneQuarter: BannerHeightOption;
    scalable: BannerHeightOption;
    threeQuarter: BannerHeightOption;
}
export type BannerHeightOptions = DataOptions<BannerHeightOption> & BannerHeightOptionMapper;
export type BannerHeight = LiteralUnion<keyof BannerHeightOptionMapper>;

export const bannerHeightOptions: BannerHeightOptions = {
    oneQuarter: { id: "1/4", displayName: "1/4 Viewport", value: "25vh", isDefault: true },
    half: { id: "1/2", displayName: "1/2 Viewport", value: "50vh" },
    threeQuarter: { id: "3/4", displayName: "3/4 Viewport", value: "75vh" },
    full: { id: "full", displayName: "Full Viewport", value: "100vh" },
    auto: { id: "auto", displayName: "Automatic", value: "" },
    scalable: { id: "scalable", displayName: "Scalable", value: "" },
    custom: { id: "custom", displayName: "Custom", value: "" },
};

export type BannerOverlayAlignmentOption = DataOption & { degree?: string };
export interface BannerOverlayAlignmentOptionMapper {
    auto: BannerOverlayAlignmentOption;
    bottom: BannerOverlayAlignmentOption;
    content: BannerOverlayAlignmentOption;
    full: BannerOverlayAlignmentOption;
    left: BannerOverlayAlignmentOption;
    right: BannerOverlayAlignmentOption;
    top: BannerOverlayAlignmentOption;
}
export type BannerOverlayAlignmentOptions = DataOptions<BannerOverlayAlignmentOption> &
    BannerOverlayAlignmentOptionMapper;
export type BannerOverlayAlignment = LiteralUnion<keyof BannerOverlayAlignmentOptionMapper>;

export const bannerOverlayAlignmentOptions: BannerOverlayAlignmentOptions = {
    auto: { id: "auto", displayName: "Automatic", isDefault: true },
    full: { id: "full", displayName: "Full" },
    left: { id: "left", displayName: "Left", degree: "-90deg" },
    right: { id: "right", displayName: "Right", degree: "90deg" },
    top: { id: "top", displayName: "Top", degree: "0deg" },
    bottom: { id: "bottom", displayName: "Bottom", degree: "180deg" },
    content: { id: "content", displayName: "Content" },
};

export type BannerVariantValue = Partial<Omit<BannerFields, "variant">>;
export type BannerVariantOption = DataOption<BannerVariantValue> & {
    hiddenFields?: (keyof BannerFields)[];
};
export interface BannerVariantOptionMapper {
    blog: BannerVariantOption;
    compact: BannerVariantOption;
    custom: BannerVariantOption;
    standard: BannerVariantOption;
}
export type BannerVariantOptions = DataOptions<BannerVariantOption> & BannerVariantOptionMapper;
export type BannerVariant = LiteralUnion<keyof BannerVariantOptionMapper>;

export const bannerVariantOptions: BannerVariantOptions = {
    standard: {
        id: "standard",
        displayName: "Standard",
        isDefault: true,
        value: {
            fullWidth: false,
            gridWidth: "[12,12,6,5,5]",
            height: bannerHeightOptions.scalable.id,
            horizontalAlignment: textAlignmentOptions.right.id,
            overlayAlignment: bannerOverlayAlignmentOptions.bottom.id,
            overlayLength: "160",
            padding: "40",
            scalingHeight: "[550]",
            scalingWidth: "[1920]",
            verticalAlignment: verticalAlignmentOptions.middle.id,
        },
    },
    compact: {
        id: "compact",
        displayName: "Compact",
        value: {
            fullWidth: false,
            gridWidth: "[12,9,7,5,5]",
            height: bannerHeightOptions.scalable.id,
            horizontalAlignment: textAlignmentOptions.left.id,
            overlayAlignment: bannerOverlayAlignmentOptions.bottom.id,
            overlayLength: "160",
            padding: "40",
            scalingHeight: "[320]",
            scalingWidth: "[1920]",
            verticalAlignment: verticalAlignmentOptions.middle.id,
        },
    },
    custom: { id: "custom", displayName: "Custom" },
    blog: {
        id: "blog",
        displayName: "Blog",
        value: {
            fullWidth: false,
            gridWidth: "[12, 12, 8, 8, 8]",
            height: bannerHeightOptions.scalable.id,
            horizontalAlignment: textAlignmentOptions.center.id,
            overlayAlignment: bannerOverlayAlignmentOptions.bottom.id,
            overlayLength: "160",
            padding: "[168, null, null, 222, null]",
            scalingHeight: "[550]",
            scalingWidth: "[1920]",
            verticalAlignment: verticalAlignmentOptions.middle.id,
        },
    },
};

const isFieldVisibleForVariant = (variant: BannerVariant, fieldName: keyof BannerVariantValue) => {
    const { hiddenFields, value } = bannerVariantOptions[variant] || {};

    return typeof value?.[fieldName] === "undefined" && !hiddenFields?.includes(fieldName);
};

interface OwnProps extends WidgetProps, ExpandStyles<BannerStyles> {
    content?: ReactNode;
    fields: BannerFields;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    videoWrapper?: InjectableCss;
    video?: VideoPresentationProps;
    innerWrapper?: InjectableCss;
    content?: InjectableCss;
    contentGridContainer?: GridContainerProps;
    contentGridItem?: GridItemProps;
    overlay?: InjectableCss;
    scaler?: InjectableCss;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            position: relative;
            display: flex;
        `,
    },
    videoWrapper: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
        `,
    },
    video: {
        extendedStyles: {
            video: {
                css: css`
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                `,
            },
        },
    },
    innerWrapper: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    content: {
        css: css`
            position: relative;
            width: 100%;

            > div:not(.banner-overlay) /* Zone */ {
                position: relative;
            }
        `,
    },
    contentGridContainer: { gap: 30 },
    contentGridItem: { width: 12 },
    overlay: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
        `,
    },
    scaler: {
        css: css`
            flex: 0 0 100%;
            margin-left: -100%;
            visibility: hidden;
            pointer-events: none;

            div::before {
                content: "";
                display: block;
            }
        `,
    },
};

export const Banner: FC<OwnProps> = ({ content, extendedStyles, fields, id, overridedStyles }) => {
    const additionalStyles = useMergeStyles("banner", bannerStyles);
    const expandedStyles = useExpandProps(additionalStyles, extendedStyles, overridedStyles);
    const hasContent = useZoneIsActive(id, "Content") || content;

    const {
        backgroundColor,
        customCSS,
        customHeight,
        focalPoint,
        fullWidth,
        gridWidth,
        hasMobileSettings,
        hasMobileVideo,
        hasVideo,
        height,
        horizontalAlignment,
        image,
        invertColor,
        mobileFocalPoint,
        mobileImage,
        mobileSpacingPercent,
        mobileVideoPoster,
        mobileVideoSourceUrl,
        mobileVideoSourceMediaLibrary,
        mobileVideoSourceType: baseMobileVieoSourceType,
        overlayAlignment,
        overlayColor,
        overlayLength,
        overlayPadding,
        overlayRadius,
        padding,
        scalingHeight,
        scalingWidth,
        showOverlay,
        verticalAlignment,
        videoPoster,
        videoSourceUrl,
        videoSourceMediaLibrary,
        videoSourceType: baseVideoSourceType,
    } = useMemo(() => overrideProps(fields, getDataOptionValue(bannerVariantOptions, fields.variant)), [fields]);

    const isAutoOverlay = overlayAlignment === bannerOverlayAlignmentOptions.auto.id;
    const isContentOverlay = overlayAlignment === bannerOverlayAlignmentOptions.content.id;

    const isMobile = useIsBreakpoint(mobileBreakpoint, "down");

    const videoSourceType = videoSourceTypeField.useParse(baseVideoSourceType);
    const mobileVideoSourceType = videoSourceTypeField.useParse(baseMobileVieoSourceType);

    const isMedia = useMemo(() => {
        return videoSourceType === videoSourceTypeOptions.media.id;
    }, [videoSourceType]);

    const isMobileMedia = useMemo(() => {
        return mobileVideoSourceType === videoSourceTypeOptions.media.id;
    }, [mobileVideoSourceType]);

    const videoSource = useMemo(() => {
        return isMedia ? videoSourceMediaLibrary : videoSourceUrl;
    }, [videoSourceMediaLibrary, videoSourceUrl, isMedia]);

    const mobileVideoSource = useMemo(() => {
        return isMobileMedia ? mobileVideoSourceMediaLibrary : mobileVideoSourceUrl;
    }, [mobileVideoSourceMediaLibrary, mobileVideoSourceUrl, isMobileMedia]);

    const videoPosterSource = useMemo(() => {
        if (isMobile && hasMobileSettings && hasMobileVideo) {
            return mobileVideoPoster;
        }
        if (isMobile && hasMobileSettings) {
            return undefined;
        }
        if (hasVideo) {
            return videoPoster;
        }
        return undefined;
    }, [isMobile, hasMobileVideo, hasMobileSettings, mobileVideoPoster, videoPoster, hasVideo]);

    const videoSrc = useMemo(() => {
        if (isMobile && hasMobileSettings && hasMobileVideo) {
            return mobileVideoSource;
        }
        if (isMobile && hasMobileSettings) {
            return undefined;
        }
        if (hasVideo) {
            return videoSource;
        }
        return undefined;
    }, [isMobile, hasMobileVideo, hasMobileSettings, mobileVideoSource, videoSource, hasVideo]);

    const wrapperStyles = useMemo(() => {
        if (isMobile && hasMobileSettings && mobileImage && !hasMobileVideo) {
            return css`
                background-size: cover;
                ${cssProp("background-image", mobileImage, x => `url(${parseImageUrl(x).url})`)}
                ${mobileFocalPoint ? getFocalPointStyles(mobileFocalPoint) : getFocalPointStyles(focalPoint)}
            `;
        }
        if (isMobile && hasMobileSettings) {
            return;
        }
        if (image && !hasVideo) {
            return css`
                background-size: cover;
                ${cssProp("background-image", image, x => `url(${parseImageUrl(x).url})`)}
                ${getFocalPointStyles(focalPoint)}
            `;
        }
    }, [isMobile, mobileImage, image, hasMobileSettings, hasMobileVideo, hasVideo, focalPoint]);

    const styles = useMemo(() => {
        const _padding = paddingField.parseToGridPreset(padding);

        return mergeProps<BannerStyles>(expandedStyles, {
            wrapper: {
                css: css`
                    ${({ theme }) => css`
                        background-color: ${resolveColor(backgroundColor || defaultBackgroundColor, theme)};
                    `}
                    ${wrapperStyles}
                    ${invertColor && typographyInvertColorStyles()}
                    ${customCSS}
                `,
            },
            video: {
                extendedStyles: {
                    video: {
                        css: css`
                            ${() => {
                                if (hasMobileSettings) {
                                    return css`
                                        ${breakpoint(mobileBreakpoint, "down")} {
                                            ${mobileFocalPoint &&
                                            getFocalPointStyles(mobileFocalPoint, "object-position")}
                                        }
                                    `;
                                }
                            }}
                            ${getFocalPointStyles(focalPoint, "object-position")}
                        `,
                    },
                },
            },
            innerWrapper: {
                css: css`
                    ${breakpointValues(_padding?.vertical, ["padding-top", "padding-bottom"])}
                    ${fullWidth && breakpointValues(_padding?.horizontal, ["padding-right", "padding-left"])}
                    ${!fullWidth && siteWidthStyles()}
                    ${() => {
                        if (isMobile && hasMobileSettings && mobileSpacingPercent) {
                            return css`
                                padding-top: ${mobileSpacingPercent}%;
                            `;
                        }
                    }}
                    ${cssProp("align-items", verticalAlignmentField.parseToFlex(verticalAlignment))}
                `,
            },
            contentGridContainer: {
                offsetCss: css`
                    ${cssProp("justify-content", horizontalAlignmentField.parseToFlex(horizontalAlignment))}
                `,
            },
            contentGridItem: { width: gridWidthField.parseToGridWidths(gridWidth) },
            content: {
                css: css`
                    ${() => {
                        if (showOverlay && isContentOverlay && overlayPadding) {
                            return css`
                                ${breakpointValues(overlayPaddingField.parse(overlayPadding), "padding")}
                            `;
                        }
                    }}
                `,
            },
            overlay: {
                css: css`
                    ${({ theme }) => {
                        const color = resolveColor(overlayColor || defaultOverlayColor, theme);
                        const transparentColor = Color(color).alpha(0).toString();
                        const alignment = isAutoOverlay ? horizontalAlignment : overlayAlignment;
                        const alignmentDegree = bannerOverlayAlignmentOptions[alignment]?.degree;

                        return css`
                            background-color: ${color};

                            ${() => {
                                if (isContentOverlay && overlayRadius) {
                                    return css`
                                        ${breakpointValues(overlayRadiusField.parse(overlayRadius), "border-radius")}
                                    `;
                                }
                            }}

                            ${() => {
                                if (alignmentDegree) {
                                    const gradient = `linear-gradient(
                                        ${alignmentDegree},
                                        ${transparentColor} -999%,
                                        ${transparentColor} {0}%,
                                        ${color} 100%
                                    );`;

                                    const fullColor = Color(color).fade(0.3).toString();

                                    if (isAutoOverlay) {
                                        let lastWidth: GridWidths;

                                        return css`
                                            ${breakpointValues(
                                                gridWidthField.parseToGridWidths(gridWidth)?.map(x => {
                                                    if (!x || x === lastWidth) {
                                                        return null;
                                                    }

                                                    lastWidth = x;

                                                    if (x === 12) {
                                                        return fullColor;
                                                    }

                                                    const percent = (1 - (x / 12) * 2) * 100;
                                                    return gradient.replace("{0}", `${percent}`);
                                                }),
                                                "background",
                                            )}
                                        `;
                                    }

                                    if (overlayLength) {
                                        return css`
                                            ${breakpointValues(
                                                overlayLengthField.parse(overlayLength)?.map(x => {
                                                    if (x === null) {
                                                        return null;
                                                    }

                                                    if (x < 0) {
                                                        return css`
                                                            background-color: ${fullColor};
                                                        `;
                                                    }

                                                    const percent = 100 - x;
                                                    return css`
                                                        background: ${gradient.replace("{0}", `${percent}`)};
                                                    `;
                                                }),
                                            )}
                                        `;
                                    }

                                    return css`
                                        background: ${gradient.replace("{0}", `0`)};
                                    `;
                                }
                            }}
                        `;
                    }}
                `,
            },
            scaler: {
                css: css`
                    ${() => {
                        if (height === bannerHeightOptions.scalable.id) {
                            const _scalingWidth = scalingWidthField.parse(scalingWidth);
                            const _scalingHeight = scalingHeightField.parse(scalingHeight);

                            if (_scalingWidth && _scalingHeight) {
                                const sizes = [];
                                let width = null;
                                let height = null;

                                for (let i = 0; i < 5 /* breakpoints */; i++) {
                                    width = _scalingWidth[i] || width;
                                    height = _scalingHeight[i] || height;
                                    sizes.push(width && height ? getImageSize(width, height) : null);
                                }

                                return css`
                                    ${breakpointValues(
                                        sizes.map(x => {
                                            if (!x) {
                                                return null;
                                            }

                                            return css`
                                                div {
                                                    max-width: ${pxToRem(x.width)};

                                                    &::before {
                                                        padding-top: ${x.ratio * 100}%;
                                                    }
                                                }
                                            `;
                                        }),
                                    )}
                                `;
                            }
                        }

                        if (height === bannerHeightOptions.custom.id) {
                            return breakpointValues(customHeightField.parse(customHeight), "min-height");
                        }

                        return cssProp("min-height", getDataOptionValue(bannerHeightOptions, height));
                    }}
                `,
            },
        });
    }, [
        backgroundColor,
        customCSS,
        customHeight,
        focalPoint,
        fullWidth,
        gridWidth,
        hasMobileSettings,
        height,
        horizontalAlignment,
        image,
        invertColor,
        isMobile,
        mobileBreakpoint,
        mobileFocalPoint,
        mobileImage,
        mobileSpacingPercent,
        overlayAlignment,
        overlayColor,
        overlayLength,
        overlayPadding,
        overlayRadius,
        padding,
        scalingHeight,
        scalingWidth,
        showOverlay,
        verticalAlignment,
    ]);

    const overlay = <StyledWrapper aria-hidden="true" className="banner-overlay" {...styles.overlay} />;

    return (
        <StyledWrapper {...styles.wrapper} className="banner">
            <StyledWrapper aria-hidden="true" {...styles.scaler} className="banner-scaler">
                <StyledWrapper />
            </StyledWrapper>
            {videoSrc ? (
                <StyledWrapper {...styles.videoWrapper}>
                    <Video
                        key={videoSrc}
                        {...styles.video}
                        autoPlay
                        controls={false}
                        loop
                        poster={videoPosterSource}
                        src={videoSrc}
                    />
                </StyledWrapper>
            ) : null}
            {showOverlay && !isContentOverlay && overlay}
            {hasContent && (
                <StyledWrapper {...styles.innerWrapper} className="banner-inner-wrapper">
                    <GridContainer {...styles.contentGridContainer}>
                        <GridItem {...styles.contentGridItem}>
                            <StyledWrapper className="banner-content" {...styles.content}>
                                {showOverlay && isContentOverlay && overlay}
                                {content || <Zone contentId={id} zoneName="Content" />}
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const defaultCustomCss = `& {
}

.banner-overlay {
}

.banner-content {
}`;

const advancedTab = { displayName: "Advanced", sortOrder: 3 };
const mobileTab = { displayName: "Mobile", sortOrder: 2 };
const overlayTab = { displayName: "Overlay", sortOrder: 1 };

const banner: WidgetModule<Toolkit.FieldDefinition> = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                ...getDataOptionsFieldDefinition(bannerVariantOptions),
                name: fields.variant,
                displayName: "Variant",
                fieldType: "Contextual",
                hideEmptyOption: true,
            },
            {
                name: fields.invertColor,
                displayName: "Invert Text Color",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.invertColor);
                },
            },
            {
                name: fields.hasVideo,
                displayName: "With Video",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                variant: "toggle",
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.hasVideo);
                },
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "",
                isVisible: ({ fields: { variant, hasVideo } }) => {
                    return !hasVideo && isFieldVisibleForVariant(variant, fields.backgroundColor);
                },
            },
            {
                name: fields.image,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                fieldType: "Contextual",
                defaultValue: "",
                isVisible: ({ fields: { variant, hasVideo } }) => {
                    return !hasVideo && isFieldVisibleForVariant(variant, fields.image);
                },
            },
            {
                ...videoSourceTypeField.definition,
                isVisible: x => x.fields.hasVideo,
            },
            {
                ...videoSourceUrlField,
                name: fields.videoSourceUrl,
                isVisible: ({ fields: { hasVideo, videoSourceType, variant } }) => {
                    return (
                        hasVideo &&
                        videoSourceType === videoSourceTypeOptions.url.id &&
                        isFieldVisibleForVariant(variant, fields.videoSourceUrl)
                    );
                },
            },
            {
                ...videoSourceMediaLibraryField,
                name: fields.videoSourceMediaLibrary,
                isVisible: ({ fields: { hasVideo, videoSourceType, variant } }) => {
                    return (
                        hasVideo &&
                        videoSourceType === videoSourceTypeOptions.media.id &&
                        isFieldVisibleForVariant(variant, fields.videoSourceMediaLibrary)
                    );
                },
            },
            {
                ...videoPosterField,
                name: fields.videoPoster,
                isVisible: ({ fields: { hasVideo, videoSourceType, variant } }) => {
                    return (
                        hasVideo &&
                        (videoSourceType === videoSourceTypeOptions.media.id ||
                            videoSourceType === videoSourceTypeOptions.url.id) &&
                        isFieldVisibleForVariant(variant, fields.videoPoster)
                    );
                },
            },
            {
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: ({ fields: { image, hasVideo, variant } }) => {
                    return (hasVideo || image) && isFieldVisibleForVariant(variant, fields.focalPoint);
                },
            },
            {
                ...getDataOptionsFieldDefinition(bannerHeightOptions),
                name: fields.height,
                displayName: "Height",
                fieldType: "Contextual",
                hideEmptyOption: true,
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.height);
                },
            },
            {
                ...scalingWidthField.definition,
                displayName: "Scaling Width",
                fieldType: "Contextual",
                isVisible: ({ fields: { height, variant } }) => {
                    return (
                        height === bannerHeightOptions.scalable.id &&
                        isFieldVisibleForVariant(variant, fields.scalingWidth)
                    );
                },
            },
            {
                ...scalingHeightField.definition,
                displayName: "Scaling Height",
                fieldType: "Contextual",
                isVisible: ({ fields: { height, variant } }) => {
                    return (
                        height === bannerHeightOptions.scalable.id &&
                        isFieldVisibleForVariant(variant, fields.scalingHeight)
                    );
                },
            },
            {
                ...customHeightField.definition,
                displayName: "Custom Height",
                fieldType: "Contextual",
                isVisible: ({ fields: { height, variant } }) => {
                    return (
                        height === bannerHeightOptions.custom.id &&
                        isFieldVisibleForVariant(variant, fields.customHeight)
                    );
                },
            },
            {
                ...paddingField.definition,
                displayName: "Padding",
                fieldType: "Contextual",
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.padding);
                },
            },
            {
                ...gridWidthField.definition,
                displayName: "Grid Layout Size",
                fieldType: "Contextual",
                tooltip: `A number between 0 and 12`,
                tooltipPosition: "left",
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.gridWidth);
                },
            },
            {
                ...horizontalAlignmentField.definition,
                displayName: "Horizontal Alignment",
                fieldType: "Contextual",
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.horizontalAlignment);
                },
            },
            {
                ...verticalAlignmentField.definition,
                displayName: "Vertical Alignment",
                fieldType: "Contextual",
                defaultValue: verticalAlignmentOptions.middle.id,
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.verticalAlignment);
                },
            },
            {
                name: fields.fullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.fullWidth);
                },
            },
            {
                name: fields.hasMobileSettings,
                displayName: "Mobile Specific Settings",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                variant: "toggle",
                tab: mobileTab,
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.hasMobileSettings);
                },
            },
            {
                name: fields.hasMobileVideo,
                displayName: "With Video",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                variant: "toggle",
                tab: mobileTab,
                isVisible: ({ fields: { variant, hasMobileSettings } }) => {
                    return hasMobileSettings && isFieldVisibleForVariant(variant, fields.hasMobileVideo);
                },
            },
            {
                name: fields.mobileImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: mobileTab,
                isVisible: ({ fields: { hasMobileSettings, hasMobileVideo, variant } }) => {
                    return (
                        !hasMobileVideo && hasMobileSettings && isFieldVisibleForVariant(variant, fields.mobileImage)
                    );
                },
            },
            {
                ...videoSourceTypeField.definition,
                name: fields.mobileVideoSourceType,
                tab: mobileTab,
                isVisible: x => x.fields.hasMobileVideo && x.fields.hasMobileSettings,
            },
            {
                ...videoSourceUrlField,
                name: fields.mobileVideoSourceUrl,
                tab: mobileTab,
                isVisible: ({ fields: { hasMobileSettings, hasMobileVideo, mobileVideoSourceType, variant } }) => {
                    return (
                        hasMobileSettings &&
                        hasMobileVideo &&
                        mobileVideoSourceType === videoSourceTypeOptions.url.id &&
                        isFieldVisibleForVariant(variant, fields.mobileVideoSourceUrl)
                    );
                },
            },
            {
                ...videoSourceMediaLibraryField,
                name: fields.mobileVideoSourceMediaLibrary,
                tab: mobileTab,
                isVisible: ({ fields: { hasMobileSettings, hasMobileVideo, mobileVideoSourceType, variant } }) => {
                    return (
                        hasMobileSettings &&
                        hasMobileVideo &&
                        mobileVideoSourceType === videoSourceTypeOptions.media.id &&
                        isFieldVisibleForVariant(variant, fields.mobileVideoSourceMediaLibrary)
                    );
                },
            },
            {
                ...videoPosterField,
                name: fields.mobileVideoPoster,
                tab: mobileTab,
                isVisible: ({ fields: { hasMobileSettings, mobileVideoSourceType, hasMobileVideo, variant } }) => {
                    return (
                        hasMobileSettings &&
                        hasMobileVideo &&
                        (mobileVideoSourceType === videoSourceTypeOptions.media.id ||
                            mobileVideoSourceType === videoSourceTypeOptions.url.id) &&
                        isFieldVisibleForVariant(variant, fields.mobileVideoPoster)
                    );
                },
            },
            {
                name: fields.mobileFocalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: mobileTab,
                isVisible: ({ fields: { hasMobileSettings, hasMobileVideo, mobileImage, variant } }) => {
                    return (
                        hasMobileSettings &&
                        (hasMobileVideo || mobileImage) &&
                        isFieldVisibleForVariant(variant, fields.mobileFocalPoint)
                    );
                },
            },
            {
                name: fields.mobileSpacingPercent,
                displayName: "Spacing Percent",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: null,
                tooltip: "Spacing (in percent) above the content.",
                validate: x => (typeof x === "number" && x < 0 ? "Value must be greater then or equal to 0" : ""),
                tab: mobileTab,
                isVisible: ({ fields: { hasMobileSettings, variant } }) => {
                    return hasMobileSettings && isFieldVisibleForVariant(variant, fields.mobileSpacingPercent);
                },
            },
            {
                name: fields.showOverlay,
                displayName: "Show Overlay",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                variant: "toggle",
                tab: overlayTab,
                isVisible: ({ fields: { variant } }) => {
                    return isFieldVisibleForVariant(variant, fields.showOverlay);
                },
            },
            {
                name: fields.overlayColor,
                displayName: "Overlay Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: overlayTab,
                isVisible: ({ fields: { showOverlay, variant } }) => {
                    return showOverlay && isFieldVisibleForVariant(variant, fields.overlayColor);
                },
            },
            {
                ...getDataOptionsFieldDefinition(bannerOverlayAlignmentOptions),
                name: fields.overlayAlignment,
                displayName: "Overlay Alignment",
                fieldType: "Contextual",
                hideEmptyOption: true,
                tab: overlayTab,
                isVisible: ({ fields: { showOverlay, variant } }) => {
                    return showOverlay && isFieldVisibleForVariant(variant, fields.overlayAlignment);
                },
            },
            {
                ...overlayLengthField.definition,
                displayName: "Overlay Length (%)",
                fieldType: "Contextual",
                tab: overlayTab,
                isVisible: ({ fields: { overlayAlignment, showOverlay, variant } }) => {
                    return (
                        showOverlay &&
                        Boolean(bannerOverlayAlignmentOptions[overlayAlignment]?.degree) &&
                        isFieldVisibleForVariant(variant, fields.overlayLength)
                    );
                },
            },
            {
                ...overlayPaddingField.definition,
                displayName: "Overlay Padding",
                fieldType: "Contextual",
                tab: overlayTab,
                isVisible: ({ fields: { overlayAlignment, showOverlay, variant } }) => {
                    return (
                        showOverlay &&
                        overlayAlignment === bannerOverlayAlignmentOptions.content.id &&
                        isFieldVisibleForVariant(variant, fields.overlayPadding)
                    );
                },
            },
            {
                ...overlayRadiusField.definition,
                displayName: "Overlay Radius",
                fieldType: "Contextual",
                tab: overlayTab,
                isVisible: ({ fields: { overlayAlignment, showOverlay, variant } }) => {
                    return (
                        showOverlay &&
                        overlayAlignment === bannerOverlayAlignmentOptions.content.id &&
                        isFieldVisibleForVariant(variant, fields.overlayRadius)
                    );
                },
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                defaultValue: defaultCustomCss,
                tab: advancedTab,
                isVisible: ({ fields: { variant } }, shouldDisplayAdvancedFeatures) => {
                    return !!shouldDisplayAdvancedFeatures && isFieldVisibleForVariant(variant, fields.customCSS);
                },
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
